import { Alert, Box, BoxProps, FormHelperText, Grid, Stack, styled, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { useGetMooring } from 'src/api/moorings'
import { DmsCoordinateFormField } from '../../../../components/IMaskInputField'
import {
  AppForm,
  AppFormProps,
  FormCheckbox,
  FormSelectField,
  FormSubmitButton,
  FormTextField,
} from '../../../../components/FormCore'
import { FormGeometryColorField } from '../../../../components/FormGeometryColorField'
import { GenericDialogLayout } from '../../../../components/GenericDialogLayout'
import { MainButton } from '../../../../components/MainButton'
import { IMooringType, IPosition, IPositionType } from '../../../../generated-types'
import { IPositionDrawing } from '../../../../generated-types/position-drawing'
import { useZodForm } from '../../../../hooks/zod-form'
import { getDefaultGeometryColor, isGeometryPositionType } from 'src/utils/position-geometry'
import {
  getMaxConnectedPositions,
  getUpdatePositionSchema,
  IPositionNameRuleContext,
  isNumberTakenByActivePosition,
  normalizePositionName,
  showLatLang,
} from '../schema'
import { AddPositionDrawingModalComponent } from './AddPositionDrawingModalComponent'
import { ConnectedPositionsFields } from './ConnectedPositionField'

export interface PositionFormProps extends AppFormProps {
  initialValues?: IPosition
  loading?: boolean
  positions?: IPosition[]
  activePositions?: IPosition[]
  title?: string
  isDialog?: boolean
  customerId: EntityId
  localityId: EntityId
}

export const PositionForm: React.FC<PositionFormProps> = ({
  onSubmit,
  onCancel,
  initialValues,
  loading,
  positions,
  activePositions,
  title,
  isDialog = true,
  customerId,
  localityId,
}) => {
  const { t } = useTranslation(['position', 'common'])
  const { showDialog } = useDialogCtx()
  const [selectedDrawing, setSelectedDrawing] = useState<IPositionDrawing | undefined>({
    id: initialValues?.positionDrawingId as number,
    drawingNumber: initialValues?.positionDrawingNumber as string,
    fileUrl: initialValues?.positionDrawingFileUrl as string,
  } as IPositionDrawing)
  const [selectedSunkDrawing, setselectedSunkDrawing] = useState<IPositionDrawing | undefined>({
    id: initialValues?.sunkPositionDrawingId as number,
    drawingNumber: initialValues?.sunkPositionDrawingNumber as string,
    fileUrl: initialValues?.sunkPositionDrawingFileUrl as string,
  } as IPositionDrawing)
  const [positionType, setPositionType] = useState<IPositionType | undefined>(initialValues?.type)
  const [isMultiPlacement, setisMultiPlacement] = useState<boolean | undefined>(initialValues?.isMultiPlacementsEnabled)

  const isUpdateForm = initialValues && initialValues.id > 0
  const { data: mooring } = useGetMooring(customerId, localityId, initialValues?.mooringId as EntityId)
  const isSteelCage = mooring?.type === IMooringType.SteelCage
  const maxConnectedPositions = getMaxConnectedPositions(
    ((initialValues?.type as IPositionType) ?? positionType) as IPositionType,
    isSteelCage,
  )
  const isReadOnly = Boolean(isUpdateForm && initialValues?.isActive === false)
  const positionTypes = Object.keys(IPositionType)
    .filter(v => isNaN(Number(v)))
    .map(t => IPositionType[t])
    .filter(
      type =>
        isUpdateForm ||
        !(
          isGeometryPositionType(type) ||
          type === IPositionType.InnerMooringLine ||
          type === IPositionType.BargeCorner
        ),
    )

  const nameRuleContext = useMemo<IPositionNameRuleContext>(
    () => ({
      activePositions: activePositions ?? [],
      currentPositionId: initialValues?.id,
      isNew: !isUpdateForm,
      wasActive: initialValues?.isActive ?? false,
      originalName: initialValues?.name,
    }),
    [activePositions, initialValues?.id, initialValues?.isActive, initialValues?.name, isUpdateForm],
  )

  const schema = useMemo(
    () => getUpdatePositionSchema((initialValues?.type as IPositionType) ?? positionType, nameRuleContext, isSteelCage),
    [initialValues?.type, isSteelCage, nameRuleContext, positionType],
  )
  const form = useZodForm(schema, {
    mode: 'onChange',
    defaultValues: {
      ...initialValues,
      name: initialValues?.name !== undefined && initialValues?.name !== null ? String(initialValues.name) : '',
      connectedPositions: initialValues?.connectedPositions ?? [],
    },
  })

  const watchedName = useWatch({ control: form.control, name: 'name' })
  const watchedIsActive = useWatch({ control: form.control, name: 'isActive' })
  const { isSubmitted } = useFormState({ control: form.control })

  const activeNumberIsTaken = useMemo(() => {
    const claimsTheNumber =
      nameRuleContext.isNew ||
      (Boolean(watchedIsActive) && !nameRuleContext.wasActive) ||
      (Boolean(watchedIsActive) &&
        nameRuleContext.wasActive &&
        normalizePositionName(watchedName) !== normalizePositionName(nameRuleContext.originalName))

    return claimsTheNumber && isNumberTakenByActivePosition(watchedName, nameRuleContext)
  }, [nameRuleContext, watchedIsActive, watchedName])

  const submitDisabled = activeNumberIsTaken || (isReadOnly && !watchedIsActive)

  useEffect(() => {
    if (form.getValues('name')) form.trigger('name')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedIsActive])

  useEffect(() => {
    if (initialValues) {
      form.reset({
        ...initialValues,
        name: initialValues?.name !== undefined && initialValues?.name !== null ? String(initialValues.name) : '',
        connectedPositions: initialValues?.connectedPositions ?? [],
        positionDrawingId: selectedDrawing?.id,
        sunkPositionDrawingId: selectedSunkDrawing?.id,
      })
      setSelectedDrawing({
        id: selectedDrawing?.id,
        drawingNumber: selectedDrawing?.drawingNumber,
        fileUrl: selectedDrawing?.fileUrl,
      } as IPositionDrawing)
    }
  }, [form, initialValues])

  useEffect(() => {
    form.setValue('positionDrawingId', selectedDrawing?.id)
    form.setValue('regenerateComponents', selectedDrawing?.regenerateComponents ?? false)
    setSelectedDrawing({
      id: selectedDrawing?.id,
      drawingNumber: selectedDrawing?.drawingNumber,
      fileUrl: selectedDrawing?.fileUrl,
      regenerateComponents: selectedDrawing?.regenerateComponents ?? false,
    } as IPositionDrawing)
  }, [selectedDrawing?.id])

  useEffect(() => {
    form.setValue('sunkPositionDrawingId', selectedSunkDrawing?.id)
    form.setValue('regenerateSunkComponents', selectedSunkDrawing?.regenerateComponents ?? false)
    setselectedSunkDrawing({
      id: selectedSunkDrawing?.id,
      drawingNumber: selectedSunkDrawing?.drawingNumber,
      fileUrl: selectedSunkDrawing?.fileUrl,
      regenerateComponents: selectedSunkDrawing?.regenerateComponents ?? false,
    } as IPositionDrawing)
  }, [selectedSunkDrawing?.id])

  const isFirstRender = useRef(true)

  useEffect(() => {
    if (positionType != IPositionType.Bridle) {
      form.setValue('sunkPositionDrawingId', null)
      form.setValue('isMultiPlacementsEnabled', false)
      form.setValue('regenerateComponents', false)
      form.setValue('regenerateSunkComponents', false)
      if (positionType === IPositionType.Cage) {
        form.setValue('isCritical', false)
      }
      setisMultiPlacement(false)
    }
  }, [positionType])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    if (positionType !== undefined) {
      const currentName = form.getValues('name')
      if (currentName || form.formState.errors.name) {
        form.trigger('name')
      }
    }
  }, [positionType])

  const renderAddPositionDrawingDialog = async (selectedDrawing, setSelectedDrawing) => {
    return await showDialog(
      AddPositionDrawingModalComponent,
      {
        componentProps: {
          customerId: customerId as EntityId,
          localityId: localityId as EntityId,
          mooringId: initialValues?.mooringId as EntityId,
          positionId: initialValues?.id,
          positionDrawingId: selectedDrawing?.id as EntityId,
          positionType: (initialValues?.type ?? positionType) as IPositionType,
          setSelectedPositionDrawing: setSelectedDrawing as any,
          isUpdateForm: isUpdateForm,
          selectedPositionDrawing: selectedDrawing,
          useSelfSubmit: false,
        },
        opts: {
          maxWidth: 'md',
          fullWidth: true,
          rootWidth: '800px',
        },
      },
      undefined,
      true,
    )
  }
  const onClickSelectPositionDrawing = useCallback(async () => {
    const added = renderAddPositionDrawingDialog(selectedDrawing, setSelectedDrawing)
    if (!added) return
  }, [
    customerId,
    localityId,
    initialValues?.mooringId,
    initialValues?.id,
    initialValues?.positionDrawingId,
    initialValues?.type,
    positionType,
    isUpdateForm,
    selectedDrawing?.id,
    setSelectedDrawing,
  ])
  const onClickSelectSunkPositionDrawing = useCallback(async () => {
    const added = renderAddPositionDrawingDialog(selectedSunkDrawing, setselectedSunkDrawing)

    if (!added) return
  }, [
    customerId,
    localityId,
    initialValues?.mooringId,
    initialValues?.id,
    initialValues?.sunkPositionDrawingId,
    initialValues?.type,
    positionType,
    isUpdateForm,
    selectedSunkDrawing?.id,
    setselectedSunkDrawing,
  ])

  const Form = useMemo(() => {
    return (
      <AppForm form={form} onSubmit={onSubmit} hasInitialValues={initialValues != undefined}>
        <div id="positionForm-drawing-select" />
        {isReadOnly && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('inactivePositionReadOnly', { ns: 'position' })}
          </Alert>
        )}
        <FormTextField name="mooringId" type="hidden" autoFocus={false} />
        <FormTextField name="positionDrawingId" type="hidden" autoFocus={false} />
        <FormTextField
          name="name"
          label={t('position', { ns: 'common' })}
          autoFocus={false}
          disabled={isReadOnly}
          onBlur={event => form.setValue('name', normalizePositionName(event.target.value), { shouldValidate: true })}
        />

        <Grid container>
          <Grid item xs={12} sm={6}>
            <FormSelectField
              name="type"
              disabled={isUpdateForm || isReadOnly ? true : false}
              data={positionTypes}
              label={t('type', { ns: 'common' })}
              getOptionLabel={option => t(IPositionType[option], { ns: 'position' })}
              renderOption={(props: any, option) => (
                <Box {...props} key={option}>
                  {t(IPositionType[option], { ns: 'position' })}
                </Box>
              )}
              onChange={(_, value) => {
                form.setValue('type', value, { shouldValidate: true })
                setPositionType(value as IPositionType)
                const currentName = form.getValues('name')
                if (currentName || form.formState.errors.name) {
                  form.trigger('name')
                }
              }}
            />
          </Grid>
          {positionType != null && positionType != IPositionType.Cage && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body1">
                {`${t('selected-drawing', { ns: 'position' })}: ${
                  selectedDrawing?.drawingNumber ?? t('n/a', { ns: 'common' })
                }`}
              </Typography>
              <StyledDrawingBox
                className={`${''} rounded-lg`}
                selectedDrawing={selectedDrawing}
                onClick={isReadOnly ? undefined : onClickSelectPositionDrawing}
              >
                {!(initialValues?.positionDrawingFileUrl ?? selectedDrawing?.fileUrl) &&
                  t('click-to-update', { ns: 'common' })}
              </StyledDrawingBox>
              {form.formState.errors.positionDrawingId && (
                <FormHelperText error={true} sx={{ textAlign: 'left' }}>
                  {t(`${form.formState.errors.positionDrawingId.message}`)}
                </FormHelperText>
              )}
            </Grid>
          )}
        </Grid>
        <Grid container>
          {positionType != null && positionType === IPositionType.Bridle && (
            <>
              <Grid item xs={12} sm={6}>
                <FormCheckbox
                  name="isMultiPlacementsEnabled"
                  label={t('isMultiPlacementsEnabled', { ns: 'position' })}
                  autoFocus={false}
                  disabled={isReadOnly}
                  onChange={(_, checked) => {
                    setisMultiPlacement(checked)
                    form.setValue('isMultiPlacementsEnabled', checked)
                    if (!checked) form.setValue('sunkPositionDrawingId', null)
                  }}
                />
              </Grid>

              {isMultiPlacement && (
                <Grid item xs={12} sm={6} paddingTop={1}>
                  <Typography variant="body1">
                    {`${t('selected-sunk-position-drawing', { ns: 'position' })}: ${
                      selectedSunkDrawing?.drawingNumber ?? t('n/a', { ns: 'common' })
                    }`}
                  </Typography>
                  <StyledDrawingBox
                    className={`${''} rounded-lg`}
                    selectedDrawing={selectedSunkDrawing}
                    onClick={isReadOnly ? undefined : onClickSelectSunkPositionDrawing}
                  >
                    {!(initialValues?.sunkPositionDrawingFileUrl ?? selectedSunkDrawing?.fileUrl) &&
                      t('click-to-update', { ns: 'common' })}
                  </StyledDrawingBox>
                  {form.formState.errors.sunkPositionDrawingId && (
                    <FormHelperText error={true} sx={{ textAlign: 'left' }}>
                      {t(`${form.formState.errors.sunkPositionDrawingId.message}`)}
                    </FormHelperText>
                  )}
                </Grid>
              )}
            </>
          )}
        </Grid>
        {positionType != null && isGeometryPositionType(positionType) && (
          <FormGeometryColorField
            name="geometry.color"
            label={t('geometry-colour', { ns: 'mooring-layout' })}
            defaultValue={initialValues?.geometry?.color ?? getDefaultGeometryColor(positionType)}
            disabled={isReadOnly}
          />
        )}
        {showLatLang(positionType as IPositionType) && (
          <Grid container>
            <Grid item xs={12} sm={6}>
              <DmsCoordinateFormField
                direction="latitude"
                name="latitude"
                label={t('latitude', { ns: 'common' })}
                disabled={isReadOnly}
                showErrors={isSubmitted}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DmsCoordinateFormField
                direction="longitude"
                name="longitude"
                label={t('longitude', { ns: 'common' })}
                disabled={isReadOnly}
                showErrors={isSubmitted}
              />
            </Grid>
          </Grid>
        )}
        {maxConnectedPositions > 0 && (
          <Stack
            direction="row"
            sx={{
              width: '49%',
            }}
          >
            <ConnectedPositionsFields
              positions={positions as IPosition[]}
              positionId={form.getValues('id')}
              name={'connectedPositions'}
              connectingPositionType={positionType}
              maxConnectedPositions={maxConnectedPositions}
              disabled={isReadOnly}
            />
            <FormHelperText id="connctedPositions-error" error>
              {form.formState.errors?.connectedPositions?.message}
            </FormHelperText>
          </Stack>
        )}
        <FormCheckbox name="isActive" label={t('active', { ns: 'common' })} autoFocus={false} />
        <FormCheckbox name="isHidden" label={t('hidden', { ns: 'common' })} autoFocus={false} disabled={isReadOnly} />
        <FormCheckbox
          name="mooringAnalyseAppliance"
          label={t('mooringAnalyseAppliance', { ns: 'position' })}
          disabled={isReadOnly}
        />
        {positionType != IPositionType.Cage && (
          <FormCheckbox
            name="isCritical"
            label={t('critical', { ns: 'common' })}
            autoFocus={false}
            disabled={isReadOnly}
          />
        )}
        <Grid container>
          <Grid item xs={12} sm={6}>
            <FormTextField
              type="number"
              name="mbl"
              label={t('minimumBreakingLoad', { ns: 'position' })}
              autoFocus={false}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormTextField
              type="number"
              name="accidentLoad"
              label={t('accident-load', { ns: 'position' })}
              autoFocus={false}
              disabled={isReadOnly}
            />
          </Grid>
          {/* {(form.getValues('type') == (IPositionType.Cage as number) || positionType == IPositionType.Cage) && (
            <Grid item xs={12} sm={6}>
              <FormTextField type="number" name="width" label={t('width', { ns: 'position' })} autoFocus={false} />
            </Grid>
          )} */}
          <Grid item xs={12} sm={6}>
            <FormTextField
              inputProps={{ type: 'number' }}
              name="depth"
              label={t('depth', { ns: 'main-component' })}
              autoFocus={false}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormTextField
              type="string"
              name="reference"
              label={t('reference', { ns: 'common' })}
              autoFocus={false}
              disabled={isReadOnly}
            />
          </Grid>
        </Grid>
        <FormTextField
          type="string"
          name="note"
          label={t('note', { ns: 'common' })}
          multiline
          rows={4}
          autoFocus={false}
          disabled={isReadOnly}
        />
        {!isDialog && (
          <Box className="flex justify-end gap-4 pt-4">
            <FormSubmitButton
              disabled={loading || submitDisabled}
              isLoading={loading}
              createText={isUpdateForm ? t('update', { ns: 'common' }) : t('add', { ns: 'common' })}
            />
          </Box>
        )}
      </AppForm>
    )
  }, [
    form,
    isUpdateForm,
    isReadOnly,
    submitDisabled,
    positionType,
    initialValues,
    isDialog,
    selectedDrawing,
    loading,
    positions,
    maxConnectedPositions,
    onClickSelectPositionDrawing,
    t,
    onSubmit,
    positionTypes,
    isSubmitted,
  ])

  return (
    <>
      {isDialog ? (
        <GenericDialogLayout
          title={title as string}
          pop={onCancel}
          removedCloseButton={false}
          actions={[
            <MainButton
              key="position-form-btn"
              loading={loading}
              disabled={submitDisabled}
              onClick={() => {
                form.handleSubmit(onSubmit)()
              }}
            >
              {isUpdateForm ? t('update', { ns: 'common' }) : t('add', { ns: 'common' })}
            </MainButton>,
          ]}
        >
          {Form}
        </GenericDialogLayout>
      ) : (
        { Form }
      )}
    </>
  )
}
interface StyledBox extends BoxProps {
  selectedDrawing: IPositionDrawing | undefined
}
const StyledDrawingBox = styled(Box)<StyledBox>(({ selectedDrawing }) => ({
  backgroundImage: `url(${selectedDrawing?.fileUrl})`,
  backgroundColor: '#EBF9FC',
  height: 100,
  width: 150,
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}))
